import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';

export interface RelatorioItem {
  funcionarioNome: string;
  clienteNome: string;
  dataAtendimento: string;
  horaInicio?: string;
  horaFim?: string;
  duracaoMinutos?: number;
  numeroReserva?: string;
  resultado?: string;
}

interface RelatorioFuncionariosPDFProps {
  items: RelatorioItem[];
  dataInicio: string;
  dataFim: string;
  funcionarioFiltro: string; // 'Todos' ou nome do funcionário
  empresaNome?: string;
  logoUrl?: string;
}

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    padding: 30,
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: 16,
    borderBottomWidth: 2,
    borderBottomColor: '#2c3e50',
    paddingBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerContent: {
    flex: 1,
  },
  logo: {
    width: 130,
    maxHeight: 90,
    objectFit: 'contain',
    marginLeft: 16,
  },
  title: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: '#2c3e50',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 9,
    color: '#7f8c8d',
  },
  filterRow: {
    flexDirection: 'row',
    marginBottom: 12,
    backgroundColor: '#ecf0f1',
    padding: 6,
    borderRadius: 4,
  },
  filterLabel: {
    fontFamily: 'Helvetica-Bold',
    color: '#2c3e50',
    marginRight: 4,
  },
  filterValue: {
    color: '#555',
  },
  filterSep: {
    marginHorizontal: 12,
    color: '#bdc3c7',
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#2c3e50',
    paddingVertical: 5,
    paddingHorizontal: 4,
  },
  tableHeaderCell: {
    color: '#ffffff',
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#dde3e8',
  },
  tableRowAlt: {
    backgroundColor: '#f8f9fa',
  },
  cell: {
    color: '#333',
    fontSize: 8,
  },
  colNum: { width: '4%' },
  colAtendente: { width: '14%' },
  colCliente: { width: '28%' },
  colData: { width: '9%' },
  colInicio: { width: '7%' },
  colFim: { width: '7%' },
  colDuracao: { width: '7%' },
  colContrato: { width: '7%' },
  colResultado: { width: '15%' },
  footer: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#bdc3c7',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: '#7f8c8d',
  },
  summaryBox: {
    marginTop: 14,
    flexDirection: 'row',
    gap: 12,
  },
  summaryItem: {
    backgroundColor: '#ecf0f1',
    padding: 6,
    borderRadius: 4,
    marginRight: 10,
    alignItems: 'center',
    minWidth: 80,
  },
  summaryNumber: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: '#2c3e50',
  },
  summaryLabel: {
    fontSize: 7,
    color: '#7f8c8d',
  },
});

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const formatDuracao = (minutos?: number) => {
  if (!minutos || minutos <= 0) return '-';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return `${m}min`;
};

const formatResultado = (resultado?: string) => {
  const map: Record<string, string> = {
    finalizou_contrato: 'Fechou Contrato',
    converteu_reserva: 'Converteu Reserva',
    nao_pagou: 'Não Pagou',
    nao_levou: 'Não Levou',
    so_orcamento: 'Só Orçamento',
    outros: 'Outros',
  };
  return resultado ? (map[resultado] || resultado) : '-';
};

const formatHora = (hora?: string) => {
  if (!hora) return '-';
  return hora.substring(0, 5); // HH:MM
};

const RelatorioFuncionariosPDF: React.FC<RelatorioFuncionariosPDFProps> = ({
  items,
  dataInicio,
  dataFim,
  funcionarioFiltro,
  empresaNome,
  logoUrl,
}) => {
  const totalContratos = items.filter(i => {
    const resultado = i.resultado?.trim();

    return (
      resultado === 'finalizou_contrato' ||
      resultado === 'converteu_reserva'
    );
  }).length;

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="landscape">
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <Text style={styles.title}>Relatório de Produtividade</Text>
            <Text style={styles.subtitle}>
              Atendimentos realizados no período
            </Text>
          </View>
          {logoUrl && (
            <Image src={logoUrl} style={styles.logo} />
          )}
        </View>

        <View style={styles.filterRow}>
          <Text style={styles.filterLabel}>Período:</Text>
          <Text style={styles.filterValue}>
            {formatDate(dataInicio)} a {formatDate(dataFim)}
          </Text>
          <Text style={styles.filterSep}>|</Text>
          <Text style={styles.filterLabel}>Funcionário:</Text>
          <Text style={styles.filterValue}>{funcionarioFiltro}</Text>
        </View>

        <View style={styles.table}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.colNum]}>#</Text>
            <Text style={[styles.tableHeaderCell, styles.colAtendente]}>Atendente</Text>
            <Text style={[styles.tableHeaderCell, styles.colCliente]}>Cliente</Text>
            <Text style={[styles.tableHeaderCell, styles.colData]}>Data</Text>
            <Text style={[styles.tableHeaderCell, styles.colInicio]}>Início</Text>
            <Text style={[styles.tableHeaderCell, styles.colFim]}>Fim</Text>
            <Text style={[styles.tableHeaderCell, styles.colDuracao]}>Duração</Text>
            <Text style={[styles.tableHeaderCell, styles.colContrato]}>Nº Contrato</Text>
            <Text style={[styles.tableHeaderCell, styles.colResultado]}>Resultado</Text>
          </View>

          {items.length === 0 && (
            <View style={[styles.tableRow]}>
              <Text style={[styles.cell, { width: '100%', textAlign: 'center', color: '#999' }]}>
                Nenhum atendimento encontrado no período selecionado.
              </Text>
            </View>
          )}

          {items.map((item, idx) => (
            <View
              key={idx}
              style={[styles.tableRow, idx % 2 === 1 ? styles.tableRowAlt : {}]}
            >
              <Text style={[styles.cell, styles.colNum]}>{idx + 1}</Text>
              <Text style={[styles.cell, styles.colAtendente]}>{item.funcionarioNome}</Text>
              <Text style={[styles.cell, styles.colCliente]}>{item.clienteNome?.toUpperCase()}</Text>
              <Text style={[styles.cell, styles.colData]}>{formatDate(item.dataAtendimento)}</Text>
              <Text style={[styles.cell, styles.colInicio]}>{item.horaInicio ? formatHora(item.horaInicio) : 'Sem registro'}</Text>
              <Text style={[styles.cell, styles.colFim]}>{item.horaFim ? formatHora(item.horaFim) : 'Sem registro'}</Text>
              <Text style={[styles.cell, styles.colDuracao]}>{formatDuracao(item.duracaoMinutos)}</Text>
              <Text style={[styles.cell, styles.colContrato]}>{item.numeroReserva ? item.numeroReserva : 'Sem contrato'}</Text>
              <Text style={[styles.cell, styles.colResultado]}>{formatResultado(item.resultado)}</Text>
            </View>
          ))}
        </View>

        <View style={styles.summaryBox}>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryNumber}>{items.length}</Text>
            <Text style={styles.summaryLabel}>Atendimentos</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryNumber}>{totalContratos}</Text>
            <Text style={styles.summaryLabel}>Contratos Fechados</Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            Gerado em {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
          />
          <Text style={styles.footerText}>
            SystemLoc
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default RelatorioFuncionariosPDF;
